import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { inviteParent, listStudents } from '../../modules/platform/api/schools';
import { Student } from '../../modules/platform/types';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { validateEmail } from '../../utils/validation';

interface InviteFormData {
  email: string;
  student_id?: string;
}

export const InviteParent: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const schoolId = user?.memberships[0]?.school_id;

  useEffect(() => {
    if (!schoolId) return;

    const loadStudents = async () => {
      try {
        const data = await listStudents(schoolId);
        setStudents(data);
      } catch (error) {
        showErrorToast(error);
      }
    };

    loadStudents();
  }, [schoolId]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormData>();

  const onSubmit = async (data: InviteFormData) => {
    if (!schoolId) return;

    setIsLoading(true);
    try {
      await inviteParent(schoolId, data.email, data.student_id);
      showSuccessToast('Invite sent successfully!');
      reset();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Invite Parent"
        subtitle="Send an invitation to a parent"
      />

      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Invite Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Email Address"
              type="email"
              {...register('email', {
                required: 'Email is required',
                validate: (value) => validateEmail(value) || 'Invalid email address',
              })}
              error={errors.email?.message}
              placeholder="parent@example.com"
              helperText="An invite will be sent to this email address"
            />

            <Select
              label="Link to Student (Optional)"
              options={[
                { value: '', label: 'None - Parent can link later' },
                ...students.map((s) => ({
                  value: s._id,
                  label: `${s.name} (${s.grade})`,
                })),
              ]}
              {...register('student_id')}
            />

            <Button type="submit" isLoading={isLoading} className="w-full">
              Send Invite
            </Button>
          </form>
        </CardContent>
      </Card>
    </Layout>
  );
};

